import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api, getTenantSlug, getApiBaseUrl } from '../api/client';

// Helper to get full URL for uploaded assets
const getAssetUrl = (path: string | undefined): string | null => {
  if (!path) return null;
  // If it's already a full URL, return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // If it's an uploaded file (starts with /uploads), prepend API base URL
  if (path.startsWith('/uploads')) {
    return `${getApiBaseUrl()}${path}`;
  }
  // Otherwise return as-is (e.g., local public assets)
  return path;
};

// =============================================================================
// TYPES
// =============================================================================

export type ColorScheme = 'default' | 'blue' | 'green' | 'purple' | 'orange' | 'red';

export interface WelcomePageData {
  title?: string;
  description?: string;
  bannerImage?: string | null;
  backgroundImage?: string | null;
  /** Background image visibility — 0 (hidden) to 1 (fully visible). Default 0.5. */
  backgroundOpacity?: number;
  showEventMarquee?: boolean;
}

/** Org social profiles rendered in the app-wide footer. All optional. */
export interface SocialLinksData {
  instagram?: string;
  facebook?: string;
  website?: string;
}

interface BrandingInfo {
  logoUrl: string;
  faviconUrl: string;
  colorScheme: ColorScheme;
  /** Org-configured default theme for visitors who haven't picked one yet. */
  defaultTheme: 'light' | 'dark' | null;
  /** Org-configured default language for visitors who haven't picked one yet. */
  defaultLanguage: string | null;
  organizationName: string | null;
  welcomePage: WelcomePageData | null;
  allowSelfRegistration: boolean;
  socialLinks: SocialLinksData;
  isLoading: boolean;
}

interface BrandingContextType extends BrandingInfo {
  refreshBranding: () => Promise<void>;
}

// =============================================================================
// DEFAULTS
// =============================================================================

const DEFAULT_LOGO_URL = '/eventik-logo-name.png';
const DEFAULT_FAVICON_URL = '/eventik-logo.png';
const DEFAULT_COLOR_SCHEME: ColorScheme = 'default';

// =============================================================================
// CONTEXT
// =============================================================================

const BrandingContext = createContext<BrandingContextType | null>(null);

export const useBranding = (): BrandingContextType => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

// =============================================================================
// PROVIDER
// =============================================================================

interface BrandingProviderProps {
  children: ReactNode;
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({ children }) => {
  const [branding, setBranding] = useState<BrandingInfo>({
    logoUrl: DEFAULT_LOGO_URL,
    faviconUrl: DEFAULT_FAVICON_URL,
    colorScheme: DEFAULT_COLOR_SCHEME,
    defaultTheme: null,
    defaultLanguage: null,
    organizationName: null,
    welcomePage: null,
    allowSelfRegistration: false,
    socialLinks: {},
    isLoading: true,
  });

  const fetchBranding = useCallback(async () => {
    const tenantSlug = getTenantSlug();

    // If not on a tenant subdomain, use defaults
    if (!tenantSlug) {
      setBranding({
        logoUrl: DEFAULT_LOGO_URL,
        faviconUrl: DEFAULT_FAVICON_URL,
        colorScheme: DEFAULT_COLOR_SCHEME,
        defaultTheme: null,
        defaultLanguage: null,
        organizationName: null,
        welcomePage: null,
        allowSelfRegistration: false,
        socialLinks: {},
        isLoading: false,
      });
      return;
    }

    try {
      const response = await api.auth.getPublicOrgInfo();
      const data = response.data as {
        name: string;
        slug: string;
        defaultLanguage: string;
        branding?: {
          logo?: string;
          favicon?: string;
          colorScheme?: ColorScheme;
          defaultTheme?: 'light' | 'dark';
        };
        welcomePage?: WelcomePageData | null;
        allowSelfRegistration?: boolean;
        socialLinks?: SocialLinksData;
      };

      setBranding({
        logoUrl: getAssetUrl(data.branding?.logo) || DEFAULT_LOGO_URL,
        faviconUrl: getAssetUrl(data.branding?.favicon) || DEFAULT_FAVICON_URL,
        colorScheme: data.branding?.colorScheme || DEFAULT_COLOR_SCHEME,
        defaultTheme: data.branding?.defaultTheme ?? null,
        defaultLanguage: data.defaultLanguage ?? null,
        organizationName: data.name,
        welcomePage: data.welcomePage ?? null,
        allowSelfRegistration: data.allowSelfRegistration ?? false,
        socialLinks: data.socialLinks ?? {},
        isLoading: false,
      });
    } catch (error) {
      // If fetch fails, use defaults
      console.error('Failed to fetch branding:', error);
      setBranding({
        logoUrl: DEFAULT_LOGO_URL,
        faviconUrl: DEFAULT_FAVICON_URL,
        colorScheme: DEFAULT_COLOR_SCHEME,
        defaultTheme: null,
        defaultLanguage: null,
        organizationName: null,
        welcomePage: null,
        allowSelfRegistration: false,
        socialLinks: {},
        isLoading: false,
      });
    }
  }, []);

  // Fetch branding on mount
  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  // Apply color scheme to document body
  useEffect(() => {
    if (!branding.isLoading) {
      document.body.setAttribute('data-color-scheme', branding.colorScheme);
    }
  }, [branding.colorScheme, branding.isLoading]);

  // Update favicon dynamically
  useEffect(() => {
    if (!branding.isLoading) {
      const existingFavicon = document.querySelector('link[rel="icon"]');
      if (existingFavicon) {
        existingFavicon.setAttribute('href', branding.faviconUrl);
      } else {
        const favicon = document.createElement('link');
        favicon.rel = 'icon';
        favicon.href = branding.faviconUrl;
        document.head.appendChild(favicon);
      }
    }
  }, [branding.faviconUrl, branding.isLoading]);

  const value: BrandingContextType = {
    ...branding,
    refreshBranding: fetchBranding,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
